/**
 * WidgetWrapper Component
 * Provides the drag handle, resize controls, and widget chrome
 */

import { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import {
  GripVertical,
  MoreVertical,
  Trash2,
  Settings,
  Maximize2,
  Minimize2,
  RefreshCw,
} from 'lucide-react';
import { WIDGET_CATALOG } from './widgetConfig';

export const WidgetWrapper = ({
  widgetId,
  widgetType,
  children,
  isEditMode,
  onRemove,
  onConfigure,
  onRefresh,
  isLoading,
  isCollapsed,
  onToggleCollapse,
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const config = WIDGET_CATALOG[widgetType];
  const Icon = config?.icon;

  return (
    <div
      className={cn(
        "h-full flex flex-col rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden transition-all duration-200",
        isEditMode && "ring-2 ring-primary/20 cursor-move",
        isHovered && isEditMode && "ring-primary/40",
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`widget-${widgetId}`}
    >
      {/* Widget Header */}
      <div className={cn(
        "flex items-center gap-2 px-4 py-2 border-b border-border/30 bg-card/50",
        isEditMode && "drag-handle cursor-grab active:cursor-grabbing"
      )}>
        {/* Drag Handle (only in edit mode) */}
        {isEditMode && (
          <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}

        {/* Widget Icon & Title */}
        {Icon && (
          <div className="p-1.5 rounded-lg bg-primary/10 flex-shrink-0">
            <Icon className="w-4 h-4 text-primary" />
          </div>
        )}
        <span className="text-sm font-medium truncate flex-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {config?.name || 'Widget'}
        </span>

        {/* Widget Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Refresh Button (always visible if handler provided) */}
          {onRefresh && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                onRefresh();
              }}
              disabled={isLoading}
              data-testid={`widget-refresh-${widgetId}`}
            >
              <RefreshCw className={cn("w-3.5 h-3.5", isLoading && "animate-spin")} />
            </Button>
          )}

          {/* Collapse Toggle */}
          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
              data-testid={`widget-collapse-${widgetId}`}
            >
              {isCollapsed ? (
                <Maximize2 className="w-3.5 h-3.5" />
              ) : (
                <Minimize2 className="w-3.5 h-3.5" />
              )}
            </Button>
          )}

          {/* More Actions Menu */}
          {(isEditMode || onConfigure) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`widget-menu-${widgetId}`}
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {onConfigure && config?.configurable && (
                  <DropdownMenuItem onClick={() => onConfigure(widgetId)}>
                    <Settings className="w-4 h-4 mr-2" />
                    Configure
                  </DropdownMenuItem>
                )}
                {isEditMode && (
                  <>
                    {onConfigure && config?.configurable && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      onClick={() => onRemove(widgetId)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove Widget
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Widget Content */}
      <div className={cn(
        "flex-1 overflow-auto p-4",
        isCollapsed && "hidden"
      )}>
        {children}
      </div>
    </div>
  );
};

export default WidgetWrapper;
